import { Box, Button, Group, Text, Title } from "@mantine/core";
import type { ExtendedUser, Todo } from "../../../utils/backend-types";
import { EMPTY_FIELD } from "../../../utils/constants";
import { Extendable } from "../../Extendable/Extendable";
import { useShouldShowStaffView } from "../../StaffViewToggle/useShouldShowStaffView";
import { TodoDescription } from "../TodoDescription/TodoDescription";
import { TodoSystemDecision, type TodoUserDecision } from "../Todos.types";
import { TodoSwitch } from "../TodoSwitch/TodoSwitch";
import { getTodoStatusOptions } from "../utils/getTodoStatusOptions";

interface TodoDecisionListProps {
  todos: Array<Todo>;
  userDecisions: Record<number, TodoUserDecision>;
  finishedTodoIds: Array<number>;
  clarifiedTodoIds: Array<number>;
  users: Array<ExtendedUser>;
  onChangeUserDecisionAndFinished: (
    todoId: number,
    userDecision: TodoUserDecision,
    finished: boolean
  ) => void;
  onClickClarify: (todo: Todo) => void;
}

function TodoDecisionList({
  todos,
  userDecisions,
  finishedTodoIds,
  clarifiedTodoIds,
  users,
  onChangeUserDecisionAndFinished,
  onClickClarify
}: TodoDecisionListProps) {
  const sortedTodos = todos.sort((a, b) => {
    if (a.overdue) {
      return -1;
    } else if (b.overdue) {
      return 1;
    } else {
      return 0;
    }
  });

  function getFullTodoLabel(todo: Todo) {
    const todoLabel = todo.label;

    if (todo.fulfillFor) {
      return `${todoLabel} - ${todo.fulfillFor}`;
    }

    return todoLabel;
  }

  function isTodoClarified(todo: Todo) {
    return clarifiedTodoIds.some(
      (clarifiedTodoId) => clarifiedTodoId === todo.id
    );
  }

  function isTodoFinished(todo: Todo) {
    return finishedTodoIds.some((finishedTodoId) => finishedTodoId === todo.id);
  }

  return (
    <div className="TodoDecisionList">
      {sortedTodos.map((todo) => (
        <Group align="flex-start" gap="xs" key={todo.id} wrap="nowrap">
          <Box flex="1">
            <Text pt="xs">{getFullTodoLabel(todo)}</Text>
            <Extendable>
              <TodoDetails todo={todo} users={users} />
            </Extendable>
          </Box>
          {todo.systemDecision === TodoSystemDecision.NeedMoreInformation &&
            !isTodoClarified(todo) && (
              <Button onClick={() => onClickClarify(todo)}>Klären</Button>
            )}
          <TodoSwitch
            finished={isTodoFinished(todo)}
            userDecision={userDecisions[todo.id]}
            onChangeUserDecision={(userDecision, finished) =>
              onChangeUserDecisionAndFinished(todo.id, userDecision, finished)
            }
          />
        </Group>
      ))}
    </div>
  );
}

interface TodoDetailsProps {
  todo: Todo;
  users: Array<ExtendedUser>;
}

function TodoDetails({ todo, users }: TodoDetailsProps) {
  const { shouldShowStaffView } = useShouldShowStaffView();
  const statusValues = getTodoStatusOptions(
    todo.status,
    todo.label,
    shouldShowStaffView
  );
  const statusValue = statusValues.find(
    (status) => status.value === todo.status
  );
  const statusLabel = statusValue?.label ?? "";
  const responsibleUser = users.find((user) => user.id === todo.responsible);
  const responsiblePerson = responsibleUser
    ? responsibleUser.name
    : EMPTY_FIELD;

  return (
    <Box className="TodoDetails" mt="xs">
      <Title fw={400} order={6}>
        Beschreibung
      </Title>
      <Box mb="md" ml="xs">
        <TodoDescription
          regulatoryDuty={todo.regulatoryDuty}
          userDefinedDescription={todo.description}
        />
      </Box>
      <Title fw={400} order={6}>
        Verpflichtetes Unternehmen
      </Title>
      <Text mb="md" ml="xs">
        {todo.person || EMPTY_FIELD}
      </Text>
      <Title fw={400} order={6}>
        Fälligkeit
      </Title>
      <Text mb="md" ml="xs">
        {todo.overdue ? "Überfällig" : todo.dueDate || EMPTY_FIELD}
      </Text>
      <Title fw={400} order={6}>
        Verantwortlich
      </Title>
      <Text mb="md" ml="xs">
        {responsiblePerson}
      </Text>
      <Title fw={400} order={6}>
        Status
      </Title>
      <Text mb="md" ml="xs">
        {statusLabel}
      </Text>
    </Box>
  );
}

export { TodoDecisionList, TodoDecisionListProps };
